import React, { useState, useEffect } from 'react';
import './EditAbout.css';
import { useOutletContext } from 'react-router-dom';

const API_URL_HOME = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-f7ce.up.railway.app';
const SLOW_THRESHOLD = 2000; // ms
const ABOUT_TTL      = 5 * 60 * 1000; // 5 min

// ── Module-level cache ────────────────────────────────────────────────────────
// Fallback cache if aboutData wasn't prefetched by EditStore yet.
const _aboutCache = new Map();

// ── Default content shape ─────────────────────────────────────────────────────
const EMPTY_CONTENT = {
  paragraphs: [], website: "", verified_page: "", industry: "",
  company: "", headquarter: "", founded: "", specialties: "",
  address: "", country: "", state: "", city: "", zipCode: "", page_type: ""
};

function _readLocalStorage() {
  try {
    const s = localStorage.getItem('cached_about_data');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function EditHome() {
  const { aboutData, pageId: ctxPageId, token: ctxToken, prefetchDone } = useOutletContext() || {};

    // Access the API URL from environment variables
    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  // ── [PERF] Seed state from localStorage immediately so UI is never blank ──
  // If EditStore's prefetch hasn't finished yet we still show the last-known
  // data instead of a loading skeleton.
  const [content, setContent] = useState(() => _readLocalStorage() || EMPTY_CONTENT);


  // Only show the skeleton if there is genuinely nothing to display yet.
  // If localStorage has data, skip it entirely — the user sees content at t=0.
  const [loading, setLoading]   = useState(!_readLocalStorage());
  const [error, setError]       = useState(null);

  useEffect(() => {
    // ── Fast path: EditStore prefetch gave us data ─────────────────────────
    // aboutData is populated by EditStore's parallel fetch. If it arrived,
    // render immediately — no network call needed at all.
    if (aboutData) {
      setContent(aboutData);
      setLoading(false);
      return;
    }

    // ── Wait for EditStore prefetch signal before issuing a fallback fetch ─
    // prefetchDone is set to true when EditStore's parallel fetch resolves
    // (success or failure). Waiting avoids a duplicate fetch racing with it.
    if (!prefetchDone) return;

    // ── Fallback: prefetch completed but aboutData is null (error/404) ────
    const controller = new AbortController();

    const fetchAboutPage = async () => {
      const token  = ctxToken  || localStorage.getItem('token');
      const pageId = ctxPageId || localStorage.getItem('pageId');

      if (!pageId) {
        console.error("[EditHome] fetchAboutPage — pageId not found");
        setLoading(false);
        return;
      }

      // Check module-level cache before hitting network
      const cached = _aboutCache.get(pageId);
      if (cached && Date.now() - cached.ts < ABOUT_TTL) {
        setContent(cached.data);
        setLoading(false);
        return;
      }

      const t0 = performance.now();
      try {
        const response = await fetch(`${API_URL_HOME}/get_about_page_for_store?pageid=${pageId}`, {
          signal:  controller.signal,
          method:  "GET",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }
        });

        const elapsed = performance.now() - t0;
        if (elapsed > SLOW_THRESHOLD) {
          console.error(
            `[SLOW][EditHome] GET /get_about_page_for_store took ${(elapsed / 1000).toFixed(2)}s ` +
            `(>${SLOW_THRESHOLD / 1000}s) pageid=${pageId}. ` +
            "Check: DB index on about_page(pageid), Railway cold-start, connection pool."
          );
        } else {
          console.debug(`[TIMING][EditHome] GET /get_about_page_for_store — ${elapsed.toFixed(0)}ms`);
        }

        if (!response.ok) {
          // 404 is expected for new pages — don't treat as an error
          if (response.status === 404) {
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const normalized = {
          paragraphs:    data.paragraphs    || [],
          website:       data.website       || "",
          verified_page: data.verified_page || "",
          industry:      data.industry      || "",
          company:       data.company       || "",
          headquarter:   data.headquarter   || "",
          founded:       data.founded       || "",
          specialties:   data.specialties   || "",
          address:       data.address       || "",
          country:       data.country       || "",
          state:         data.state         || "",
          city:          data.city          || "",
          zipCode:       data.zipcode       || "",
          page_type:     data.page_type     || ""
        };

        _aboutCache.set(pageId, { data: normalized, ts: Date.now() });
        setContent(normalized);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error("[EditHome] fetchAboutPage error:", err);
        setError("Failed to load page info.");
      } finally {
        setLoading(false);
      }
    };

    fetchAboutPage();
    return () => controller.abort();
  }, [aboutData, prefetchDone, ctxPageId, ctxToken]);

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="about_container-edit">
        <div className="about_content-edit">
          <style>{`
            .skel {
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 200% 100%;
              animation: skel-shimmer 1.2s infinite;
              border-radius: 4px;
            }
            @keyframes skel-shimmer {
              0%   { background-position: 200% 0 }
              100% { background-position: -200% 0 }
            }
          `}</style>
          <div className="header-container-edit">
            <div className="skel" style={{ height: 24, width: 100, marginBottom: 16 }} />
          </div>
          <div className="skel" style={{ height: 14, width: '100%', marginBottom: 8 }} />
          <div className="skel" style={{ height: 14, width: '90%',  marginBottom: 8 }} />
          <div className="skel" style={{ height: 14, width: '95%',  marginBottom: 24 }} />
          <div className="skel" style={{ height: 16, width: 80,     marginBottom: 8 }} />
          <div className="skel" style={{ height: 14, width: '60%',  marginBottom: 24 }} />
          <div className="skel" style={{ height: 16, width: 80,     marginBottom: 8 }} />
          <div className="skel" style={{ height: 14, width: '40%' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="about_container-edit">
        <div className="about_content-edit">
          <p style={{ color: 'red' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="about_container-edit">
      <div className="about_content-edit">
        <div className="header-container-edit">
          <h2>Overview</h2>
        </div>

        {content.paragraphs && content.paragraphs.map((para, index) => (
          <div key={index} className="paragraph-container-edit">
            <p className="para-edit">{para}</p>
          </div>
        ))}

        {content.page_type === "Firm" && (
          <>
            <h3>Website</h3>
            <a href={content.website} target="_blank" rel="noopener noreferrer">
              {content.website || "No website provided"}
            </a>

            <h3>Verified Page</h3>
            <p>{content.verified_page || ""}</p>

            <h3>Industry</h3>
            <p>{content.industry || ""}</p>

            <h3>Company Size</h3>
            <p>{content.company || ""}</p>

            <h3>Founded</h3>
            <p>{content.founded || ""}</p>

            <h3>Headquarters</h3>
            <p>{content.headquarter || ""}</p>

            <h3>Specialties</h3>
            <p>{content.specialties || ""}</p>
          </>
        )}

        <h3>Address</h3>
        {content.address || content.city || content.state || content.zipCode || content.country ? (
          <div>
            {content.address  && <p>Address: {content.address}</p>}
            {content.city     && <p>City: {content.city}</p>}
            {content.state    && <p>State: {content.state}</p>}
            {content.zipCode  && <p>Zip Code: {content.zipCode}</p>}
            {content.country  && <p>Country: {content.country}</p>}
          </div>
        ) : (
          <p>No address information provided.</p>
        )}
      </div>
    </div>
  );
}

export default EditHome;